import React, { useState } from 'react'
import PropTypes from 'prop-types'
import * as Styled from './Cluster.styles'

// hooks && helpers
import {
  AUTOCOMPLETE_SELECT_WITH_ADDRESS_INDEX,
  mappedClusterTitles,
} from 'settings/app/constants.js'
import {
  parseHighlightedString,
  getHighlightedString,
} from 'settings/app/helpers'
import { TextContext } from 'hocs'

/**
 * render the cluster matches
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const Cluster = (props) => {
  const { title, matches, matchClicked, maxMatches, order } = props

  const [numberOfMatches, setNumberOfMatches] = useState(
    !maxMatches ? matches.length : maxMatches
  )
  const [showAllMatchesBtn, setShowAllMatchesBtn] = useState(
    maxMatches && numberOfMatches < matches.length
  )
  const textContext = React.useContext(TextContext)

  React.useEffect(() => {
    setShowAllMatchesBtn(maxMatches && numberOfMatches < matches.length)
  }, [maxMatches, numberOfMatches, matches])

  const handleShowAllMatches = () => {
    setNumberOfMatches(matches.length)
    setShowAllMatchesBtn(false)
  }

  const getShowAllMatchesBtnTitle = () =>
    matches.length - maxMatches > 1
      ? `${ matches.length - maxMatches } ${
          textContext['cluster.showAllMatches.btn.plural']
        }`
      : `1 ${ textContext['cluster.showAllMatches.btn.singular'] }`

  const getCyDataString = (match) => {
    if (match.source?.address?.city && match.source?.address?.zip) {
      return JSON.stringify({
        city: match.source.address.city,
        zip: match.source?.address?.zip,
      })
    }
    return false
  }

  return (
    <Styled.Cluster
      data-cy-cluster={ mappedClusterTitles[title] }
      data-cy-order={ order }
      data-cy-matches={ matches.length }>
      <div className='title'>{mappedClusterTitles[title]}</div>
      {matches.slice(0, numberOfMatches).map((match, index) => (
        <Styled.Match
          key={ index }
          onClick={ () => matchClicked(match.source.name, match.key, match.type) }
          tabIndex='0'
          onKeyDown={ (e) =>
            e.key === 'Enter'
              ? matchClicked(match.source.name, match.key, match.type)
              : null
          }
          data-cy-cluster-child={ mappedClusterTitles[title] }
          data-cy-cluster-child-order={ index }
          data-cy-cluster-child-value={ match.source.name }
          data-cy-address={ getCyDataString(match) }>
          <Styled.MatchIcon className='icon-f-arrow-right1' />
          <span>
            <>
              {parseHighlightedString(getHighlightedString(match), index)}
              {AUTOCOMPLETE_SELECT_WITH_ADDRESS_INDEX.includes(match.type) && (
                <span>
                  ({match.source.address.zip} {match.source.address.city})
                </span>
              )}
            </>
          </span>
        </Styled.Match>
      ))}
      {showAllMatchesBtn && (
        <Styled.ShowAllMatchesButton
          id={ `autocomplete-more-btn-${ title }` }
          data-cy={ `autocomplete-more-btn-${ title }` }
          text={ getShowAllMatchesBtnTitle() }
          onClick={ () => handleShowAllMatches() }
        />
      )}
    </Styled.Cluster>
  )
}

Cluster.defaultProps = {
  maxMatches: false,
}

Cluster.propTypes = {
  /** Cluster title */
  title: PropTypes.string.isRequired,
  /** Matches for the cluster */
  matches: PropTypes.arrayOf(
    PropTypes.shape({
      source: PropTypes.shape({
        code: PropTypes.string,
        type: PropTypes.string,
        name: PropTypes.string.isRequired,
      }),
      highlight: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.shape({
          code: PropTypes.arrayOf(PropTypes.string),
          type: PropTypes.arrayOf(PropTypes.string),
          name: PropTypes.arrayOf(PropTypes.string),
        }),
      ]),
    })
  ).isRequired,
  /**
   * Fired when match was clicked
   * Signature: function(match: string) {...}
   */
  matchClicked: PropTypes.func.isRequired,
  /** Limit for initial match rendering **/
  maxMatches: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  /** provides the key number for cypress oder testing **/
  order: PropTypes.number,
}

export default Cluster
